import { useMutation, useQueryClient } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';

// our axios
import axiosInstance from 'services/API/API';

// Our Endpoints
import { getUserLiabilitiesUrl as savePersonalLiabilityURL } from 'shared/api-urls';

// Our Routes
import {
	ADVISOR_BASE_NEW_LOAN_SELECTION_ROUTE,
	CLIENT_ADD_NEW_PERSONAL_ROUTE,
	CLIENT_NEW_LOAN_SELECTION_ROUTE,
	LIABILITY_ADDITIONAL_ROUTE
} from 'routes';

// Our Query keys
import {
	CLIENT_OVERVIEW_DATA,
	LIABILITY,
	WALLET_OVERVIEW_DATA,
	USER_LIABILITIES,
	SORA_INSIGHTS
} from 'shared/query-keys';

function useMutateSavePersonalLiability() {
	const location = useLocation();
	const { pathname } = location;
	const { tradeLineId } = useParams();
	const queryClient = useQueryClient();

	// Booleans
	const isManualEntry = pathname.includes(LIABILITY_ADDITIONAL_ROUTE);
	const clientEnteredThroughAddNewLiabilityFlow = pathname.includes(
		CLIENT_NEW_LOAN_SELECTION_ROUTE
	);
	const advisorEnteredThroughAddNewLiabilityFlow = pathname.includes(
		ADVISOR_BASE_NEW_LOAN_SELECTION_ROUTE
	);
	const onClientPersonalPage = pathname.includes(
		CLIENT_ADD_NEW_PERSONAL_ROUTE
	);

	const urlForRequest =
		isManualEntry ||
		clientEnteredThroughAddNewLiabilityFlow ||
		advisorEnteredThroughAddNewLiabilityFlow ||
		onClientPersonalPage
			? savePersonalLiabilityURL
			: `${savePersonalLiabilityURL}${tradeLineId}`;

	return useMutation(async (personalLiabilityData) => {
		if (
			isManualEntry ||
			clientEnteredThroughAddNewLiabilityFlow ||
			advisorEnteredThroughAddNewLiabilityFlow ||
			onClientPersonalPage
		) {
			// overwriting data confidence here.
			const personalPayloadWithRateManuallyUpdated = {
				...personalLiabilityData,
				rateManuallyUpdated: true
			};

			// manual entry is a post call
			const response = await axiosInstance.post(
				urlForRequest,
				personalPayloadWithRateManuallyUpdated
			);

			await queryClient.invalidateQueries(USER_LIABILITIES);
			await queryClient.invalidateQueries(WALLET_OVERVIEW_DATA, {
				refetchInactive: true
			});
			await queryClient.invalidateQueries(CLIENT_OVERVIEW_DATA, {
				refetchInactive: true
			});

			await queryClient.invalidateQueries(LIABILITY, {
				refetchInactive: true
			});

			await queryClient.invalidateQueries(SORA_INSIGHTS, {
				refetchInactive: true
			});

			return response.data;
		}

		// dynamic entries will be patch calls
		const response = await axiosInstance.patch(
			urlForRequest,
			personalLiabilityData
		);

		await queryClient.invalidateQueries(WALLET_OVERVIEW_DATA, {
			refetchInactive: true
		});

		await queryClient.invalidateQueries(LIABILITY, {
			refetchInactive: true
		});

		await queryClient.invalidateQueries(SORA_INSIGHTS, {
			refetchInactive: true
		});

		return response.data;
	});
}

export default useMutateSavePersonalLiability;
